// Mirrors functions/src/models/WheelPrize.ts (kept in sync manually — this admin app and the
// functions app are separate TypeScript projects with no shared package).
export type WheelPrizeType = "facilCoins" | "product" | "raffleEntry" | "cash" | "coupon" | "bonusSpin" | "custom"

export const WHEEL_PRIZE_TYPES: WheelPrizeType[] = [
  "facilCoins",
  "product",
  "raffleEntry",
  "cash",
  "coupon",
  "bonusSpin",
  "custom",
]

export const WHEEL_PRIZE_TYPE_LABELS: Record<WheelPrizeType, string> = {
  facilCoins: "Facil-Coins",
  product: "Product (sponsor service)",
  raffleEntry: "Raffle entry",
  cash: "Cash",
  coupon: "Coupon",
  bonusSpin: "Bonus spin",
  custom: "Custom",
}

export const MAX_BONUS_SPINS_PER_PRIZE = 3

export interface WheelPrizeFormData {
  id: string
  type: WheelPrizeType
  label: string
  shortLabel: string | null
  description: string | null
  imageUrl: string | null
  weight: number
  enabled: boolean
  sortOrder: number
  announceWinner: boolean | null
  featured: boolean
  grandPrize: boolean
  // Whether landing on this prize counts as an actual win (vs. a "better luck next time" /
  // "try again" segment). Drives the YOU WON vs. Try Again copy shown in the app and whether
  // a winner announcement can ever fire for this prize.
  winning: boolean
  startAtMillis: number | null
  endAtMillis: number | null
  totalQuantity: number | null
  quantityPerDay: number | null
  perUserLimit: number | null

  // facilCoins
  amount?: number
  // product
  sponsorId?: string
  serviceId?: string
  serviceTitle?: string | null
  // raffleEntry
  raffleId?: string
  ticketsCount?: number
  // cash
  currency?: string
  fulfillmentInstructions?: string | null
  // coupon
  offerId?: string | null
  offerTitle?: string | null
  // bonusSpin
  spinsAwarded?: number
  // custom
  ctaLabel?: string | null
  referenceUrl?: string | null
  adminNotes?: string | null
}

export interface WinnerNotificationsFormData {
  enabled: boolean
  scope: "all" | "grandPrizeOnly" | "selectedTypes" | "disabled"
  selectedTypes: WheelPrizeType[]
  template: string
  privacyPolicy: "firstNameLastInitial" | "anonymous"
}

export interface PresentationFormData {
  title: string
  subtitle: string
  bannerImageUrl: string | null
  // Overrides the default gift-box icon on the floating launcher button (Home screen FAB that
  // opens the wheel). Accepts a static image or an animated .gif; falls back to the gift icon
  // when unset.
  fabIconUrl: string | null
  // Overrides the launcher button's circular background color. Null falls back to the app
  // theme's `secondary.main` (see win-facil-web-app's userStore.ts theme config).
  fabBackgroundColor: string | null
  wheelSize: "compact" | "standard" | "large"
  celebrationIntensity: "subtle" | "normal" | "festive"
  soundEnabledByDefault: boolean
  showPrizePool: boolean
  showOdds: boolean
  showRemainingSpins: boolean
  accentColor: string
  spinButtonText: string
  spinButtonPlacement: "centerCircle" | "topButton" | "bottomButton"
}

export interface WheelLocationFormData {
  country?: string
  state?: string
  city?: string
}

export interface WheelConfigFormData {
  // Admin-only label, and the location/language scope this wheel applies to — lets an admin
  // create several wheels (e.g. "US - English", "US - Spanish") the way Featured Businesses
  // and Raffles scope their content. Unset location fields / unset language act as wildcards;
  // `isDefault` marks the fallback wheel used when nothing more specific matches.
  name: string
  location: WheelLocationFormData
  language: string
  isDefault: boolean
  isEnabled: boolean
  dailySpinLimit: number
  cooldownMinutes: number
  campaignStartAtMillis: number | null
  campaignEndAtMillis: number | null
  dailyGrandPrizeLimit: number | null
  prizes: WheelPrizeFormData[]
  presentation: PresentationFormData
  winnerNotifications: WinnerNotificationsFormData
}

export const DEFAULT_PRESENTATION: PresentationFormData = {
  title: "La Ruleta Win",
  subtitle: "Your daily chance to win prizes, raffle entries, cash & more.",
  bannerImageUrl: null,
  fabIconUrl: null,
  fabBackgroundColor: null,
  wheelSize: "standard",
  celebrationIntensity: "normal",
  soundEnabledByDefault: true,
  showPrizePool: true,
  showOdds: false,
  showRemainingSpins: true,
  accentColor: "#009A56",
  spinButtonText: "SPIN",
  spinButtonPlacement: "centerCircle",
}

export const DEFAULT_WINNER_NOTIFICATIONS: WinnerNotificationsFormData = {
  enabled: false,
  scope: "disabled",
  selectedTypes: [],
  template: "{name} just won {prize} on La Ruleta Win!",
  privacyPolicy: "firstNameLastInitial",
}

const createPrizeId = () => `prize-${Date.now()}-${Math.floor(Math.random() * 100_000)}`

export const createBlankPrize = (type: WheelPrizeType, sortOrder: number): WheelPrizeFormData => {
  const prize: WheelPrizeFormData = {
    id: createPrizeId(),
    type,
    label: "",
    shortLabel: null,
    description: null,
    imageUrl: null,
    weight: 10,
    enabled: true,
    sortOrder,
    announceWinner: null,
    featured: false,
    grandPrize: false,
    winning: true,
    startAtMillis: null,
    endAtMillis: null,
    totalQuantity: null,
    quantityPerDay: null,
    perUserLimit: null,
  }

  if (type === "facilCoins") {
    prize.amount = 10
  }
  if (type === "cash") {
    prize.amount = 10
    prize.currency = "USD"
  }
  if (type === "raffleEntry") {
    prize.ticketsCount = 1
  }
  if (type === "bonusSpin") {
    prize.spinsAwarded = 1
  }

  return prize
}

export const DEFAULT_PRIZES: WheelPrizeFormData[] = [
  { ...createBlankPrize("facilCoins", 0), id: "coins-5", label: "5 Facil-Coins", amount: 5, weight: 40 },
  { ...createBlankPrize("facilCoins", 1), id: "coins-10", label: "10 Facil-Coins", amount: 10, weight: 30 },
  { ...createBlankPrize("facilCoins", 2), id: "coins-25", label: "25 Facil-Coins", amount: 25, weight: 20 },
  { ...createBlankPrize("facilCoins", 3), id: "coins-100", label: "100 Facil-Coins", amount: 100, weight: 10 },
]

export const DEFAULT_CONFIG: WheelConfigFormData = {
  name: "New wheel",
  location: {},
  language: "",
  isDefault: false,
  isEnabled: false,
  dailySpinLimit: 1,
  cooldownMinutes: 0,
  campaignStartAtMillis: null,
  campaignEndAtMillis: null,
  dailyGrandPrizeLimit: null,
  prizes: DEFAULT_PRIZES,
  presentation: DEFAULT_PRESENTATION,
  winnerNotifications: DEFAULT_WINNER_NOTIFICATIONS,
}
